import React, { useRef } from 'react';
import cn from 'classnames';
import type { StrictModifiers } from '@popperjs/core';

import Popover, { PopoverRef, PlacementTypes } from '@dealroadshow/uikit/core/components/Popover';
import IconClose from '@dealroadshow/uikit/core/components/Icon/IconClose';
import Button from '@dealroadshow/uikit/core/components/Button';
import UpgradePlanButton from '@/finsight/ui/common/components/UpgragePlanButton';
import LogInButton from '@/finsight/ui/common/components/LogInButton/LogInButton';

import styles from './styles.scss';
import explanationStyles from '@/Framework/UI/Atoms/Explanation/Explanation.scss';

export enum RestrictedButtonType {
  LogIn = 'LogIn',
  UpgradePlan = 'UpgradePlan',
}

interface IProps {
  children?: React.ReactElement,
  title: string,
  description?: string,
  isOpen?: boolean,
  closeTooltip?: () => void,
  wrapperClassName?: string,
  popperModifiers?: Array<StrictModifiers>,
  actionButtonType?: RestrictedButtonType,
  customButton?: React.ElementType,
  popoverContentClass?: string,
  placement?: PlacementTypes,
}

const RestrictedAccessExplanation = ({
  children,
  title,
  description,
  isOpen,
  closeTooltip,
  wrapperClassName,
  popperModifiers = [],
  actionButtonType = RestrictedButtonType.UpgradePlan,
  customButton: CustomButton,
  popoverContentClass,
  placement,
}: IProps) => {
  const popoverRef = useRef<PopoverRef>(null);

  const renderButton = () => {
    if (CustomButton) {
      return <CustomButton />;
    }

    if (actionButtonType === RestrictedButtonType.LogIn) {
      return (
        <LogInButton
          dataTest="restrictedAccessLoginButton"
          className={ styles.logInBtn }
          withIcon={ false }
        />
      );
    }

    return <UpgradePlanButton />;
  };

  const popoverContent = (
    <div className={ styles.popoverContent }>
      <Button
        type="button"
        className={ cn(styles.closeBtn, explanationStyles.lightThemeCloseButton) }
        onClick={ closeTooltip }
        dataTest="restrictedAccessTooltipCloseButton"
      >
        <IconClose />
      </Button>
      <h3 className={ styles.title } data-test="restrictedAccessExplanationTitle">{ title }</h3>
      <div
        className={ styles.description }
        dangerouslySetInnerHTML={ { __html: description } }
        data-test="restrictedAccessExplanationDescription"
      />
      { renderButton() }
    </div>
  );

  return (
    <Popover
      ref={ popoverRef }
      content={ popoverContent }
      className={ styles.popover }
      arrowClassName={ styles.popoverArrow }
      contentClassName={ cn(styles.popoverContainer, popoverContentClass) }
      wrapperClassName={ cn(styles.wrapper, wrapperClassName) }
      renderOnlyVisible
      isVisible={ isOpen }
      dataTest="restrictedAccessExplanation"
      afterClose={ closeTooltip }
      modifiers={ popperModifiers }
      placement={ placement }
    >
      { children }
    </Popover>
  );
};

export default RestrictedAccessExplanation;
